import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from 'src/app/shared/shared.module';
import { TranslocoService } from '@jsverse/transloco';
import { ToastService } from 'src/app/shared/services/toast.service';
import { ContactModel } from 'src/app/modules/contact/models/contact.model';
import { FoundReportPdfRepository, SendFoundReportBundlePayload } from 'src/app/modules/animals/states/pdf.repository';
import { AnimalMediaModel } from 'src/app/modules/animals/models/animal-media.model';
import { AnimalModel } from 'src/app/modules/animals/models/animal.model';
import { AnimalImagesRepository } from 'src/app/modules/animals/states/animal-images.repository';
import { AnimalOriginRepository } from 'src/app/modules/animals/states/animal-origin.repository';
import { forkJoin, Observable, of, switchMap, take } from 'rxjs';

export interface BundleSendItem {
  animalOriginId: number;
  animal: AnimalModel;
}

/**
 * Versendet EINEN Fundtier-Sammelbericht für mehrere Tiere (Original + Duplikate).
 * Empfänger werden einmal gewählt; Medien werden pro Tier zugeordnet.
 * authorityInformation wird vor dem Versand per PUT auf jedes Origin gespeichert.
 */
@Component({
  selector: 'app-send-found-report-bundle-dialog',
  templateUrl: './send-found-report-bundle-dialog.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendFoundReportBundleDialogComponent implements OnChanges {
  /** Tiere des Bündels (Original + Duplikate) mit ihrem jeweiligen Fundtier-Origin. */
  @Input() items: BundleSendItem[] = [];
  /** Kontakte der zuständigen Behörde. */
  @Input() availableContacts: ContactModel[] = [];
  /** Vorbelegung der Behörden-Information (ein geteiltes Feld). */
  @Input() authorityInformation: string | null = null;

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() sent = new EventEmitter<void>();

  // ----- geteilt -----
  selectedContactIds: number[] = [];
  customEmails: string[] = [''];
  editedAuthorityInformation: string | null = null;
  isSending = false;

  // ----- pro Tier (key = animalOriginId bzw. animalId) -----
  mediaIdsByOrigin: Record<number, number[]> = {};
  existingMediaByAnimal: Record<number, AnimalMediaModel[]> = {};

  // ----- Mediathek-Modal -----
  showExistingMedia = false;
  activeItem: BundleSendItem | null = null;
  selectedExistingMediaIds = new Set<number>();

  constructor(
    private readonly _pdfRepo: FoundReportPdfRepository,
    private readonly _toast: ToastService,
    private readonly _transloco: TranslocoService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _animalImageRepo: AnimalImagesRepository,
    private readonly _animalOriginRepo: AnimalOriginRepository
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.reset();
      this.loadExistingMedia();
    }
  }

  private reset(): void {
    this.selectedContactIds = [];
    this.customEmails = [''];
    this.isSending = false;
    this.mediaIdsByOrigin = {};
    this.selectedExistingMediaIds.clear();
    this.showExistingMedia = false;
    this.activeItem = null;
    this.editedAuthorityInformation = this.authorityInformation ?? null;
  }

  private loadExistingMedia(): void {
    const animalIds = Array.from(new Set(
      this.items.map(it => it.animal?.id).filter((x): x is number => x != null)
    ));
    if (!animalIds.length) {
      this.existingMediaByAnimal = {};
      this._cdr.markForCheck();
      return;
    }
    forkJoin(animalIds.map(id => this._animalImageRepo.getImages(id).pipe(take(1))))
      .subscribe(results => {
        const map: Record<number, AnimalMediaModel[]> = {};
        animalIds.forEach((id, i) => { map[id] = results[i] ?? []; });
        this.existingMediaByAnimal = map;
        this._cdr.markForCheck();
      });
  }

  // ---------- Medien pro Tier ----------
  onMediaUploaded(item: BundleSendItem, ids: number[]): void {
    const cur = new Set<number>(this.mediaIdsByOrigin[item.animalOriginId] ?? []);
    ids.forEach(id => cur.add(id));
    this.mediaIdsByOrigin = { ...this.mediaIdsByOrigin, [item.animalOriginId]: Array.from(cur) };

    const animalId = item.animal?.id;
    if (animalId) {
      this._animalImageRepo.getImages(animalId).pipe(take(1)).subscribe(media => {
        this.existingMediaByAnimal = { ...this.existingMediaByAnimal, [animalId]: media ?? [] };
        this._cdr.markForCheck();
      });
    } else {
      this._cdr.markForCheck();
    }
  }

  existingMediaFor(item: BundleSendItem): AnimalMediaModel[] {
    return this.existingMediaByAnimal[item.animal?.id ?? -1] ?? [];
  }

  hasExistingMedia(item: BundleSendItem): boolean {
    return this.existingMediaFor(item).length > 0;
  }

  selectedMediaModelsFor(item: BundleSendItem): AnimalMediaModel[] {
    const ids = this.mediaIdsByOrigin[item.animalOriginId] ?? [];
    return this.existingMediaFor(item).filter(m => ids.includes(m.id));
  }

  removeMedia(item: BundleSendItem, id: number): void {
    const ids = (this.mediaIdsByOrigin[item.animalOriginId] ?? []).filter(mid => mid !== id);
    this.mediaIdsByOrigin = { ...this.mediaIdsByOrigin, [item.animalOriginId]: ids };
    this._cdr.markForCheck();
  }

  openExistingMediaModal(item: BundleSendItem): void {
    this.activeItem = item;
    this.selectedExistingMediaIds.clear();
    this.showExistingMedia = true;
    this._cdr.markForCheck();
  }

  closeExistingMediaModal(): void {
    this.showExistingMedia = false;
    this.activeItem = null;
    this.selectedExistingMediaIds.clear();
    this._cdr.markForCheck();
  }

  toggleExistingMedia(mediaId: number): void {
    if (this.selectedExistingMediaIds.has(mediaId)) {
      this.selectedExistingMediaIds.delete(mediaId);
    } else {
      this.selectedExistingMediaIds.add(mediaId);
    }
    this._cdr.markForCheck();
  }

  isExistingMediaSelected(mediaId: number): boolean {
    return this.selectedExistingMediaIds.has(mediaId);
  }

  addSelectedExistingMedia(): void {
    if (!this.activeItem) {
      this.closeExistingMediaModal();
      return;
    }
    const originId = this.activeItem.animalOriginId;
    const cur = new Set<number>(this.mediaIdsByOrigin[originId] ?? []);
    for (const id of this.selectedExistingMediaIds) cur.add(id);
    this.mediaIdsByOrigin = { ...this.mediaIdsByOrigin, [originId]: Array.from(cur) };
    this.closeExistingMediaModal();
  }

  itemLabel(item: BundleSendItem, index: number): string {
    const a = item.animal;
    return a?.name?.trim() || a?.bookNumber || `#${index + 1}`;
  }

  // ---------- Empfänger ----------
  get contactsWithEmail(): ContactModel[] {
    return this.availableContacts.filter(c => !!c.email?.trim());
  }

  isContactSelected(contactId: number): boolean {
    return this.selectedContactIds.includes(contactId);
  }

  toggleContact(contactId: number): void {
    if (this.isContactSelected(contactId)) {
      this.selectedContactIds = this.selectedContactIds.filter(id => id !== contactId);
    } else {
      this.selectedContactIds = [...this.selectedContactIds, contactId];
    }
  }

  addCustomEmail(): void {
    this.customEmails = [...this.customEmails, ''];
  }

  removeCustomEmail(index: number): void {
    if (this.customEmails.length === 1) {
      this.customEmails = [''];
      return;
    }
    if (index === 0) {
      const updated = [...this.customEmails];
      updated[0] = '';
      this.customEmails = updated;
    } else {
      this.customEmails = this.customEmails.filter((_, i) => i !== index);
    }
  }

  updateCustomEmail(index: number, value: string): void {
    const updated = [...this.customEmails];
    updated[index] = value;
    this.customEmails = updated;
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  getContactDisplayName(contact: ContactModel): string {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.companyName || '';
    return name ? `${name} (${contact.email})` : contact.email ?? '';
  }

  get canSend(): boolean {
    const hasSelectedContact = this.selectedContactIds.length > 0;
    const hasValidCustomEmail = this.customEmails.some(e => this.isValidEmail(e));
    return (hasSelectedContact || hasValidCustomEmail) && !this.isSending && this.items.length > 0;
  }

  // ---------- Senden ----------
  send(): void {
    if (!this.canSend) return;

    this.isSending = true;
    this._cdr.markForCheck();

    const auth = this.editedAuthorityInformation ?? null;
    const payload: SendFoundReportBundlePayload = {
      items: this.items.map(it => ({
        animalOriginId: it.animalOriginId,
        mediaIds: this.mediaIdsByOrigin[it.animalOriginId] ?? [],
      })),
      recipientContactIds: this.selectedContactIds,
      recipientCustoms: this.customEmails.map(e => e.trim()).filter(e => this.isValidEmail(e)),
    };

    // authorityInformation vor dem Versand auf jedes Origin speichern (Backend liest es von dort)
    const puts = this.items.map(it =>
      this._animalOriginRepo.update(it.animalOriginId, { authorityInformation: auth }, it.animal?.id)
    );

    const persist$: Observable<unknown> = puts.length ? forkJoin(puts) : of([]);
    persist$
      .pipe(switchMap(() => this._pdfRepo.sendBundle(payload)), take(1))
      .subscribe((result: any) => {
        this.isSending = false;
        if (result !== null) {
          // Betroffene Origins neu laden, damit das "PDF versendet"-Badge ohne Reload erscheint
          this.refreshOrigins();
          this._toast.showSuccess(this._transloco.translate('foundReport.send.toast.success'));
          this.sent.emit();
          this.close();
        }
        this._cdr.markForCheck();
      });
  }

  private refreshOrigins(): void {
    const animalIds = Array.from(new Set(
      this.items.map(it => it.animal?.id).filter((x): x is number => x != null)
    ));
    animalIds.forEach(id =>
      this._animalOriginRepo.getByAnimalId(id, /*cached*/ false).pipe(take(1)).subscribe()
    );
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
